import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateVoicePolicy from './queries/useCreateVoicePolicy';
import useGetVoicePolicy from './queries/useGetVoicePolicy';
import useUpdateVoicePolicy from './queries/useUpdateVoicePolicy';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    breadCrumbTrailEdit,
    addHeader,
    editHeader,
    initialState,
    voicePoliciesPath,
} from './constants';
import { voicePolicySchema } from './schema';
import VoicePolicyNameField from './formFields/VoicePolicyNameField';
import PatternField from './formFields/PatternField';

const VoicePolicyForm = ({ mode = 'add' }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const {
        data: voicePolicyData,
        isLoading: voicePolicyLoading,
        isFetching: voicePolicyFetching,
        isError: notFound,
    } = useGetVoicePolicy(id, isEdit);

    const handleSuccess = () => {
        toast.success(
            isEdit
                ? 'Voice Policy updated successfully!'
                : 'Voice Policy created successfully!',
        );
        navigate(voicePoliciesPath);
    };

    const handleError = (err) => {
        console.error(
            `Failed to ${isEdit ? 'update' : 'create'} voice policy:`,
            err,
        );
        setError(
            err?.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} voice policy. Please try again.`,
        );
    };

    const { mutate: createVoicePolicy, isPending: createPending } =
        useCreateVoicePolicy(handleSuccess, handleError);

    const { mutate: updateVoicePolicy, isPending: updatePending } =
        useUpdateVoicePolicy(handleSuccess, handleError);

    const isPending = isEdit ? updatePending : createPending;

    const finalFormSubmit = async (values, { setErrors, setTouched }) => {
        setError(null);

        let validated;
        try {
            validated = await voicePolicySchema.validate(values, {
                abortEarly: false,
            });
        } catch (validationError) {
            const fieldErrors = {};
            (validationError.inner || []).forEach((innerError) => {
                if (innerError.path && !fieldErrors[innerError.path]) {
                    fieldErrors[innerError.path] = innerError.message;
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                setTouched(
                    Object.keys(fieldErrors).reduce(
                        (touchedFields, field) => ({
                            ...touchedFields,
                            [field]: true,
                        }),
                        {},
                    ),
                );
            } else {
                setError(validationError.message);
            }
            return;
        }

        if (isEdit) {
            updateVoicePolicy({
                ...voicePolicyData,
                id,
                name: validated.name,
                pattern: validated.pattern,
            });
        } else {
            createVoicePolicy({
                name: validated.name,
                pattern: validated.pattern,
            });
        }
    };

    const formFields = (errors, touched) =>
        [
            <VoicePolicyNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <PatternField
                key="pattern"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (isEdit && (voicePolicyLoading || voicePolicyFetching)) {
        return <LoadingIndicator />;
    }

    if (isEdit && notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrailEdit}
                header={editHeader}
                initialValues={{ name: '', pattern: '' }}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[voicePoliciesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This voice policy no longer exists. It may have already
                        been deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const formInitialValues = isEdit
        ? {
              name: voicePolicyData?.name || '',
              pattern: voicePolicyData?.pattern || '',
          }
        : initialState;

    return (
        <FormBase
            isSubmitDisabled={isPending}
            isLoading={isPending}
            breadCrumbTrail={isEdit ? breadCrumbTrailEdit : breadCrumbTrail}
            header={isEdit ? editHeader : addHeader}
            initialValues={formInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[voicePoliciesPath, { state: { index: 0 } }]}>
            {({ errors, touched }) => formFields(errors, touched)}
        </FormBase>
    );
};

export default VoicePolicyForm;
